import { EventEmitter, Input, Output, Directive, inject, signal, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Photo } from '@models/photo';
import { AttacheService } from '@services/attache';
import { Cable } from '@services/cable';
import { BehaviorSubject } from 'rxjs';
import { PhotoVerificationResponse } from '../interfaces/photo-verification-response.interface';
import * as ActionCable from '@rails/actioncable';
import { Session } from '../queries/generated';
import { AttacheOptions } from '@models/attache-options';
import { Tokbox } from '@models/tokbox';

interface Geometry {
  width: number;
  height: number;
}

@Directive()
export abstract class AbstractVerifyPhotoDirective implements OnInit {
  @Input() session: Session;
  @Input() channel: ActionCable.Channel;
  @Output() photoTaken = new EventEmitter<Photo>();
  @Output() next = new EventEmitter();
  @Output() back = new EventEmitter();

  photo: Photo;
  photoURL: SafeUrl;
  saving = false;
  photoConfirmed = true; // ID needs confirmation from the user before proceeding
  tokbox: Tokbox;

  photoGeometry: Geometry = { width: 1600, height: 1200 };

  // Shared photo verification properties
  photo$ = new BehaviorSubject<Photo>(null);
  photoVerificationResponse$ = new BehaviorSubject<PhotoVerificationResponse>(null);
  identificationChannel: ActionCable.Channel;
  photoVerificationLoading = signal(false);
  showTryAgainButton = true;

  private attache = inject(AttacheService);
  private sanitizer = inject(DomSanitizer);
  private cable = inject(Cable);

  get liveAgentConnected(): boolean {
    if (this.tokbox) {
      return this.tokbox.connected;
    }
    return false;
  }

  get showNextButton(): boolean {
    return !this.liveAgentConnected;
  }

  get nextDisabled(): boolean {
    return this.liveAgentConnected || this.saving || !this.photo || !this.photoConfirmed;
  }

  ngOnInit() {
    if (this.session && this.session.tokbox) {
      this.tokbox = new Tokbox(this.session.tokbox);
    }
  }

  canShowBack() {
    if (this.session.liveAgent) {
      return false;
    }
    return !this.session.idVerified && this.session.requiresIdentification;
  }

  done() {
    this.next.emit();
  }

  previous() {
    this.back.emit();
  }

  capture(photo: Photo) {
    this.saving = true;
    this.photo = photo;
    this.photoURL = this.sanitizer.bypassSecurityTrustUrl(photo.image);
    this.attache
      .upload(new AttacheOptions(this.session.attacheOptions), photo.file)
      .then(
        (payload) => {
          this.photo.payload = payload;
          this.handlePhoto(this.photo);
          this.saving = false;
          this.photoTaken.emit(this.photo);
        },
        (err) => console.error('failed uploading photo', err)
      )
      .catch(() => (this.saving = false));
  }

  reset() {
    if (this.photo) {
      URL.revokeObjectURL(this.photo.image);
    }

    this.photo = null;
    this.saving = false;

    this.photo$.next(null);
    this.photoVerificationResponse$.next(null);
  }

  abstract handlePhoto(photo: Photo);

  protected subscribeToIdentificationChannel(token: string): ActionCable.Channel {
    const channel = 'IdentificationChannel';

    return this.cable.subs.create(
      { token, channel },
      {
        received: (data: PhotoVerificationResponse) => {
          /// If the photo is valid and doesn't need confirmation, we can skip the confirmation step.
          if (data.success && !data.need_confirmation) {
            this.photoConfirmed = true;
          }

          // Hide the default "try again" button when verification fails,
          // we'll show a custom "try again" button instead.
          this.showTryAgainButton = data.success;
          this.photoVerificationLoading.set(false);
          this.photoVerificationResponse$.next(data);
        }
      }
    );
  }

  protected unsubscribeFromIdentificationChannel() {
    this.identificationChannel.unsubscribe();
  }
}
